import type { Country, EntityId, MediaType, PersonCredit } from "../types/media";
import type { Rating, RatingHistoryEntry } from "../types/rating";
import { isUnknownArray, isTimestamp } from "../types/data";
import type { DateValue } from "../types/data";
import type { PersonConditionType, QuestCardCriteria, QuestCondition, QuestConditionType, WeeklyQuestCard, WeeklyQuestState } from "../types/games";
import countries from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";
import ru from "i18n-iso-countries/langs/ru.json";
import {
  hasImportMarker,
  isGameProgressEligible,
  isShortFilm
} from "./gameProgressEligibility";
import {
  isComposerCrewMember,
  isDirectorCrewMember
} from "./compactFilmCredits";
import { RATING_PROGRESS_CAST_LIMIT } from "./ratingPeople";

countries.registerLocale(en);
countries.registerLocale(ru);

const asArray = <T>(value: T | T[] | null | undefined): T[] => isUnknownArray(value) ? value : value ? [value] : [];

export const WEEKLY_QUEST_CARD_LIMITS = {
  cardsPerWeek: 7,
  maxOpened: 5,
  maxSelected: 3
};

export const WEEKLY_QUEST_CARD_GENERATOR_VERSION = 5;

export const WEEKLY_QUEST_RARITIES = {
  common: {
    label: "Обычная",
    icon: "●",
    reward: 10,
    conditionCount: 1
  },
  rare: {
    label: "Редкая",
    icon: "◆",
    reward: 30,
    conditionCount: 2
  },
  epic: {
    label: "Эпическая",
    icon: "✦",
    reward: 120,
    conditionCount: 3
  },
  legendary: {
    label: "Легендарная",
    icon: "♛",
    reward: 450,
    conditionCount: 4
  }
};

const FALLBACK_SOURCE_FILMS: { id: string; title: string; conditions: QuestCondition[] }[] = [
  {
    id: "157336",
    title: "Интерстеллар",
    conditions: [
      { type: "country", value: "сша", label: "США", aliases: ["usa", "us", "united states", "united states of america"] },
      { type: "genre", value: "фантастика", label: "фантастика", aliases: ["science fiction", "sci-fi", "sci fi"] },
      { type: "year", value: 2014, label: "2014 год" },
      { type: "decade", value: 2010, label: "2010-е" },
      { type: "director", value: "кристофер нолан", label: "режиссёр Кристофер Нолан", aliases: ["christopher nolan"] },
      { type: "actor", value: "мэттью макконахи", label: "актёр Мэттью Макконахи", aliases: ["matthew mcconaughey"] },
      { type: "composer", value: "ханс циммер", label: "композитор Ханс Циммер", aliases: ["hans zimmer"] }
    ]
  },
  {
    id: "603",
    title: "Матрица",
    conditions: [
      { type: "country", value: "сша", label: "США", aliases: ["usa", "us", "united states", "united states of america"] },
      { type: "genre", value: "фантастика", label: "фантастика", aliases: ["science fiction", "sci-fi", "sci fi"] },
      { type: "year", value: 1999, label: "1999 год" },
      { type: "decade", value: 1990, label: "1990-е" },
      { type: "director", value: "лана вачовски", label: "режиссёр Лана Вачовски", aliases: ["lana wachowski"] },
      { type: "actor", value: "киану ривз", label: "актёр Киану Ривз", aliases: ["keanu reeves"] },
      { type: "composer", value: "дон дэвис", label: "композитор Дон Дэвис", aliases: ["don davis"] }
    ]
  },
  {
    id: "496243",
    title: "Паразиты",
    conditions: [
      { type: "country", value: "южная корея", label: "Южная Корея", aliases: ["south korea", "korea", "kr"] },
      { type: "genre", value: "триллер", label: "триллер", aliases: ["thriller"] },
      { type: "year", value: 2019, label: "2019 год" },
      { type: "decade", value: 2010, label: "2010-е" },
      { type: "director", value: "пон джун-хо", label: "режиссёр Пон Джун-хо", aliases: ["bong joon-ho", "bong joon ho"] },
      { type: "actor", value: "сон кан-хо", label: "актёр Сон Кан-хо", aliases: ["song kang-ho", "song kang ho", "сон ган-хо", "сон ган хо"] },
      { type: "composer", value: "чон джэ-иль", label: "композитор Чон Джэ-иль", aliases: ["jung jae-il", "jung jae il"] }
    ]
  },
  {
    id: "194",
    title: "Амели",
    conditions: [
      { type: "country", value: "франция", label: "Франция", aliases: ["france", "fr"] },
      { type: "genre", value: "комедия", label: "комедия", aliases: ["comedy"] },
      { type: "year", value: 2001, label: "2001 год" },
      { type: "decade", value: 2000, label: "2000-е" },
      { type: "director", value: "жан-пьер жене", label: "режиссёр Жан-Пьер Жене", aliases: ["jean-pierre jeunet", "jean pierre jeunet"] },
      { type: "actor", value: "одри тоту", label: "актёр Одри Тоту", aliases: ["audrey tautou"] },
      { type: "composer", value: "янн тирсен", label: "композитор Янн Тирсен", aliases: ["yann tiersen"] }
    ]
  },
  {
    id: "129",
    title: "Унесённые призраками",
    conditions: [
      { type: "country", value: "япония", label: "Япония", aliases: ["japan", "jp"] },
      { type: "genre", value: "мультфильм", label: "мультфильм", aliases: ["animation", "anime"] },
      { type: "year", value: 2001, label: "2001 год" },
      { type: "decade", value: 2000, label: "2000-е" },
      { type: "director", value: "хаяо миядзаки", label: "режиссёр Хаяо Миядзаки", aliases: ["hayao miyazaki"] },
      { type: "actor", value: "руми хиираги", label: "актёр Руми Хиираги", aliases: ["rumi hiiragi"] },
      { type: "composer", value: "дзё хисаиси", label: "композитор Дзё Хисаиси", aliases: ["joe hisaishi", "jo hisaishi"] }
    ]
  },
  {
    id: "27205",
    title: "Начало",
    conditions: [
      { type: "country", value: "сша", label: "США", aliases: ["usa", "us", "united states", "united states of america"] },
      { type: "genre", value: "фантастика", label: "фантастика", aliases: ["science fiction", "sci-fi", "sci fi"] },
      { type: "year", value: 2010, label: "2010 год" },
      { type: "decade", value: 2010, label: "2010-е" },
      { type: "director", value: "кристофер нолан", label: "режиссёр Кристофер Нолан", aliases: ["christopher nolan"] },
      { type: "actor", value: "леонардо дикаприо", label: "актёр Леонардо ДиКаприо", aliases: ["leonardo dicaprio", "leonardo di caprio"] },
      { type: "composer", value: "ханс циммер", label: "композитор Ханс Циммер", aliases: ["hans zimmer"] }
    ]
  },
  {
    id: "680",
    title: "Криминальное чтиво",
    conditions: [
      { type: "country", value: "сша", label: "США", aliases: ["usa", "us", "united states", "united states of america"] },
      { type: "genre", value: "криминал", label: "криминал", aliases: ["crime"] },
      { type: "year", value: 1994, label: "1994 год" },
      { type: "decade", value: 1990, label: "1990-е" },
      { type: "director", value: "квентин тарантино", label: "режиссёр Квентин Тарантино", aliases: ["quentin tarantino"] },
      { type: "actor", value: "джон траволта", label: "актёр Джон Траволта", aliases: ["john travolta"] }
    ]
  },
  {
    id: "13",
    title: "Форрест Гамп",
    conditions: [
      { type: "country", value: "сша", label: "США", aliases: ["usa", "us", "united states", "united states of america"] },
      { type: "genre", value: "драма", label: "драма", aliases: ["drama"] },
      { type: "year", value: 1994, label: "1994 год" },
      { type: "decade", value: 1990, label: "1990-е" },
      { type: "director", value: "роберт земекис", label: "режиссёр Роберт Земекис", aliases: ["robert zemeckis"] },
      { type: "actor", value: "том хэнкс", label: "актёр Том Хэнкс", aliases: ["tom hanks"] }
    ]
  },
  {
    id: "550",
    title: "Бойцовский клуб",
    conditions: [
      { type: "country", value: "сша", label: "США", aliases: ["usa", "us", "united states", "united states of america"] },
      { type: "genre", value: "драма", label: "драма", aliases: ["drama"] },
      { type: "year", value: 1999, label: "1999 год" },
      { type: "decade", value: 1990, label: "1990-е" },
      { type: "director", value: "дэвид финчер", label: "режиссёр Дэвид Финчер", aliases: ["david fincher"] },
      { type: "actor", value: "брэд питт", label: "актёр Брэд Питт", aliases: ["brad pitt"] }
    ]
  },
  {
    id: "155",
    title: "Тёмный рыцарь",
    conditions: [
      { type: "country", value: "сша", label: "США", aliases: ["usa", "us", "united states", "united states of america"] },
      { type: "genre", value: "боевик", label: "боевик", aliases: ["action"] },
      { type: "year", value: 2008, label: "2008 год" },
      { type: "decade", value: 2000, label: "2000-е" },
      { type: "director", value: "кристофер нолан", label: "режиссёр Кристофер Нолан", aliases: ["christopher nolan"] },
      { type: "actor", value: "кристиан бейл", label: "актёр Кристиан Бейл", aliases: ["christian bale"] }
    ]
  },
  {
    id: "278",
    title: "Побег из Шоушенка",
    conditions: [
      { type: "country", value: "сша", label: "США", aliases: ["usa", "us", "united states", "united states of america"] },
      { type: "genre", value: "драма", label: "драма", aliases: ["drama"] },
      { type: "year", value: 1994, label: "1994 год" },
      { type: "decade", value: 1990, label: "1990-е" },
      { type: "director", value: "фрэнк дарабонт", label: "режиссёр Фрэнк Дарабонт", aliases: ["frank darabont"] },
      { type: "actor", value: "тим роббинс", label: "актёр Тим Роббинс", aliases: ["tim robbins"] }
    ]
  },
  {
    id: "238",
    title: "Крёстный отец",
    conditions: [
      { type: "country", value: "сша", label: "США", aliases: ["usa", "us", "united states", "united states of america"] },
      { type: "genre", value: "криминал", label: "криминал", aliases: ["crime"] },
      { type: "year", value: 1972, label: "1972 год" },
      { type: "decade", value: 1970, label: "1970-е" },
      { type: "director", value: "фрэнсис форд коппола", label: "режиссёр Фрэнсис Форд Коппола", aliases: ["francis ford coppola"] },
      { type: "actor", value: "марлон брандо", label: "актёр Марлон Брандо", aliases: ["marlon brando"] }
    ]
  },
  {
    id: "603692",
    title: "Джон Уик 4",
    conditions: [
      { type: "country", value: "сша", label: "США", aliases: ["usa", "us", "united states", "united states of america"] },
      { type: "genre", value: "боевик", label: "боевик", aliases: ["action"] },
      { type: "year", value: 2023, label: "2023 год" },
      { type: "decade", value: 2020, label: "2020-е" },
      { type: "director", value: "чад стахелски", label: "режиссёр Чад Стахелски", aliases: ["chad stahelski"] },
      { type: "actor", value: "киану ривз", label: "актёр Киану Ривз", aliases: ["keanu reeves"] }
    ]
  }
];

const normalizeText = (value: unknown) =>
  String(value || "")
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[‐‑‒–—-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const stripDiacritics = (value: unknown) =>
  String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const hasReadableLetters = (value: unknown) =>
  /[A-Za-zА-Яа-яЁё]/.test(stripDiacritics(value));

const PERSON_ALIAS_GROUPS = [
  ["сон кан хо", "сон ган хо", "сон кан хо хо", "song kang ho", "song kang-ho"]
];

type PersonInput = PersonCredit | string | null | undefined;

const readablePersonName = (item: PersonInput) => {
  if (!item) return "";
  if (typeof item === "string") return hasReadableLetters(item) ? item : "";
  return [item.name, item.original_name]
    .filter(Boolean)
    .find(hasReadableLetters) || "";
};

const personNameCandidates = (item: PersonInput) => {
  if (!item) return [];
  if (typeof item === "string") return hasReadableLetters(item) ? [item] : [];
  return [
    item.name,
    item.original_name,
    item.originalName,
    item.english_name
  ].filter((name): name is string => Boolean(name)).filter(hasReadableLetters);
};

const getPersonId = (item: PersonInput) =>
  item && typeof item === "object"
    ? item.id ?? item.personId ?? item.person_id ?? item.tmdbId ?? item.tmdb_id
    : null;

const normalizePersonId = (value: unknown) => {
  if (value == null) return "";
  const normalized = String(value).trim();
  return normalized && normalized !== "0" ? normalized : "";
};

const uniqueBy = <T, K>(items: T[], keyGetter: (item: T) => K): T[] => {
  const map = new Map<K, T>();
  items.forEach((item) => {
    const key = keyGetter(item);
    if (key && !map.has(key)) map.set(key, item);
  });
  return [...map.values()];
};

const uniquePeople = (items: (PersonCredit | PersonCredit[] | null | undefined)[]) => uniqueBy(
  asArray(items).flatMap(asArray).filter((person): person is PersonCredit => Boolean(person)),
  (item) => normalizePersonId(getPersonId(item)) || normalizeText(readablePersonName(item))
);

export const getWeekStart = (date = new Date()) => {
  const result = new Date(date);
  const day = result.getDay();
  const diff = result.getDate() - day + (day === 0 ? -6 : 1);
  result.setDate(diff);
  result.setHours(0, 0, 0, 0);
  return result;
};

export const getWeekEnd = (date = new Date()) => {
  const start = getWeekStart(date);
  const end = new Date(start);
  end.setDate(start.getDate() + 7);
  return end;
};

export const getCurrentWeekId = (date = new Date()) => {
  const start = getWeekStart(date);
  const year = start.getFullYear();
  const firstThursday = new Date(year, 0, 4);
  const firstWeekStart = getWeekStart(firstThursday);
  const week = Math.floor((start.getTime() - firstWeekStart.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;
  return `${year}-W${String(week).padStart(2, "0")}`;
};

export const getLegendaryChance = (misses = 0) =>
  Math.min(0.001 * (1.5 ** Math.max(0, Number(misses) || 0)), 0.5);

const getYear = (rating: Rating) => {
  const raw = rating?.releaseYear || rating?.releaseDate || rating?.release_date || rating?.filmReleaseDate || rating?.first_air_date;
  const year = Number(String(raw || "").slice(0, 4));
  return Number.isFinite(year) && year >= 1800 && year <= 2100 ? year : null;
};

const getGenres = (rating: Rating) => asArray(rating?.genres || rating?.filmGenres)
  .map((item) => typeof item === "string" ? item : item?.name)
  .filter(Boolean);

const countryLabel = (item: string | (Country & { countryCode?: string; code?: string }) | null | undefined) => {
  if (!item) return "";
  const rawValue = typeof item === "string"
    ? item
    : item.name || item.iso_3166_1 || item.countryCode || item.code || "";
  const trimmed = String(rawValue || "").trim();
  if (!trimmed) return "";

  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    return countries.getName(trimmed.toUpperCase(), "ru") || trimmed;
  }

  const titleCase = trimmed.replace(/\b\w/g, (char) => char.toUpperCase());
  const alpha2Code =
    countries.getAlpha2Code(trimmed, "en") ||
    countries.getAlpha2Code(trimmed, "ru") ||
    countries.getAlpha2Code(titleCase, "en") ||
    countries.getAlpha2Code(titleCase, "ru");

  return alpha2Code
    ? countries.getName(alpha2Code.toUpperCase(), "ru") || trimmed
    : trimmed;
};

const getCountries = (rating: Rating) => [
  ...asArray(rating?.originCountry),
  ...asArray(rating?.origin_country),
  ...asArray(rating?.productionCountry),
  ...asArray(rating?.productionCountries),
  ...asArray(rating?.production_countries)
].map(countryLabel).filter(Boolean);

const getCastPeople = (rating: Rating) => uniquePeople([
  rating?.cast,
  rating?.credits?.cast,
  rating?.filmData?.cast,
  rating?.filmData?.credits?.cast,
  rating?.fullDataFilm?.cast,
  rating?.fullDataFilm?.credits?.cast,
  rating?.details?.cast,
  rating?.details?.credits?.cast
]);

const getCrewPeople = (rating: Rating) => uniquePeople([
  rating?.crew,
  rating?.credits?.crew,
  rating?.filmData?.crew,
  rating?.filmData?.credits?.crew,
  rating?.fullDataFilm?.crew,
  rating?.fullDataFilm?.credits?.crew,
  rating?.details?.crew,
  rating?.details?.credits?.crew
]);

const getPeopleNames = (people: PersonInput[]) => uniqueBy(
  asArray(people).flatMap(personNameCandidates),
  (name) => normalizeText(name)
);

const getDirectors = (rating: Rating) => uniquePeople([
  rating?.directors,
  getCrewPeople(rating).filter(isDirectorCrewMember)
]);

const getActors = (rating: Rating) =>
  getCastPeople(rating).slice(0, RATING_PROGRESS_CAST_LIMIT);

const getComposers = (rating: Rating) => uniquePeople([
  rating?.composers,
  getCrewPeople(rating).filter(isComposerCrewMember)
]);

const condition = (type: QuestConditionType, value: string | number, label: string, aliases: string[] = [], extra: Pick<QuestCondition, "personId"> = {}): QuestCondition => ({
  type,
  value,
  label,
  aliases,
  ...extra
});

const personCondition = (type: PersonConditionType, person: PersonInput, prefix: string) => {
  const name = readablePersonName(person);
  if (!name) return null;
  const normalizedName = normalizeText(name);
  const personId = normalizePersonId(getPersonId(person));
  const aliases = getPeopleNames([person])
    .filter((item) => normalizeText(item) !== normalizedName);

  return condition(
    type,
    normalizedName,
    `${prefix} ${name}`,
    aliases,
    personId ? { personId } : {}
  );
};

const personConditions = (type: PersonConditionType, people: PersonInput[], prefix: string, limit: number) => uniqueBy(
  asArray(people).slice(0, limit).map((person) => personCondition(type, person, prefix)).filter((item): item is QuestCondition => item !== null),
  (item) => `${item.type}:${item.personId || item.value}`
);

const conditionsFromRating = (rating: Rating) => {
  const year = getYear(rating);
  return uniqueBy([
    ...getCountries(rating).map((value) => condition("country", normalizeText(value), value)),
    ...getGenres(rating).map((value) => condition("genre", normalizeText(value), value)),
    ...(year ? [
      condition("year", year, `${year} год`),
      condition("decade", Math.floor(year / 10) * 10, `${Math.floor(year / 10) * 10}-е`)
    ] : []),
    ...personConditions("director", getDirectors(rating), "режиссёр", 10),
    ...personConditions("actor", getActors(rating), "актёр", 12),
    ...personConditions("composer", getComposers(rating), "композитор", 10)
  ], (item) => `${item.type}:${item.value}`);
};

const randomItem = <T>(items: T[]): T | undefined => items[Math.floor(Math.random() * items.length)];

const pickRarity = (legendaryReserved: boolean) => {
  if (legendaryReserved) return "legendary";
  const roll = Math.random();
  if (roll < 0.01) return "epic";
  if (roll < 0.11) return "rare";
  return "common";
};

const getRatingTitle = (rating: Rating) =>
  rating?.title || rating?.name || rating?.filmTitle || rating?.originalTitle || rating?.original_name || "";

const getRatingId = (rating: Rating) =>
  rating?.filmId || rating?.id || rating?.tmdbId || rating?.filmData?.id || "";

const getRatingMediaType = (rating: Rating) =>
  rating?.mediaType ||
  rating?.media_type ||
  rating?.filmData?.mediaType ||
  rating?.filmData?.media_type ||
  (rating?.first_air_date || isUnknownArray(rating?.seasons) ? "tv" : "movie");

const uniqueRatingSources = (sources: Rating[] = []) => uniqueBy(
  asArray(sources).filter(Boolean),
  (rating: Rating) => String(getRatingId(rating) || getRatingTitle(rating)).toLowerCase()
);

const getFilmIdKey = (value: EntityId | null | undefined) => String(value || "").trim();

const toQuestRatingMillis = (value: DateValue | undefined): number => {
  const date = isTimestamp(value) ? value.toDate() : value instanceof Date ? new Date(value) :
    value && (typeof value === "string" || typeof value === "number") ? new Date(value) : null;
  const time = date?.getTime?.();
  return time !== undefined && Number.isFinite(time) ? time : 0;
};

const getQuestRatingUpdatedAt = (rating: Rating = {}) =>
  toQuestRatingMillis(rating.updatedAt || rating.firstTrackedAt || rating.lastRatedAt || rating.firstRatedAt);

const getQuestDeletionUpdatedAt = (rating: Rating = {}) =>
  toQuestRatingMillis(rating.updatedAt || rating.deletedAt || rating.lastRatedAt || rating.ratedAt || rating.firstTrackedAt);

export const getWeeklyQuestProgressRatings = (ratings: Rating[] = [], ratingHistory: RatingHistoryEntry[] = []) => {
  const historyByFilmId = new Map(
    asArray(ratingHistory)
      .filter(Boolean)
      .map((entry): [string, RatingHistoryEntry] => [getFilmIdKey(getRatingId(entry)), entry])
      .filter(([filmId]) => Boolean(filmId))
  );

  return asArray(ratings)
    .filter((rating: Rating) => rating && rating.active !== false)
    .map((rating: Rating) => {
      const filmId = getFilmIdKey(getRatingId(rating));
      const historyEntry = filmId ? historyByFilmId.get(filmId) : null;
      const ratingLifecycleId = String(rating?.ratingLifecycleId || "");
      const historyLifecycleId = String(historyEntry?.ratingLifecycleId || "");
      const sameLifecycle =
        !ratingLifecycleId ||
        !historyLifecycleId ||
        ratingLifecycleId === historyLifecycleId;
      const staleDeletionTombstone =
        historyEntry?.active === false &&
        (
          !sameLifecycle ||
          getQuestDeletionUpdatedAt(historyEntry) <= getQuestRatingUpdatedAt(rating)
        );
      const effectiveHistory = staleDeletionTombstone ? null : historyEntry;

      // A deletion tombstone wins over stale active cache, but not over the
      // same film added again after deletion.
      if (
        effectiveHistory?.active === false &&
        sameLifecycle &&
        getQuestDeletionUpdatedAt(effectiveHistory) > getQuestRatingUpdatedAt(rating)
      ) {
        return null;
      }

      return {
        ...(effectiveHistory || {}),
        ...rating,
        firstTrackedAt:
          effectiveHistory?.firstTrackedAt ||
          rating.firstTrackedAt ||
          effectiveHistory?.firstRatedAt ||
          rating.firstRatedAt ||
          rating.ratedAt,
        firstProgressMode:
          effectiveHistory?.firstProgressMode ||
          effectiveHistory?.progressMode ||
          rating.firstProgressMode ||
          rating.progressMode,
        firstImportSource:
          effectiveHistory?.firstImportSource ||
          effectiveHistory?.importSource ||
          rating.firstImportSource ||
          rating.importSource ||
          null,
        firstStreakEligible:
          effectiveHistory?.firstStreakEligible ??
          effectiveHistory?.streakEligible ??
          rating.firstStreakEligible ??
          rating.streakEligible,
        active: true
      };
    })
    .filter((rating): rating is NonNullable<typeof rating> => rating !== null);
};

const conditionFilterKey = (item: { type: string; }) =>
  item.type === "year" || item.type === "decade" ? "time" : item.type;

const uniqueConditionFilterCount = (conditions: QuestCondition[]) =>
  new Set(conditions.map(conditionFilterKey)).size;

const hasLegendaryCore = (conditions: QuestCondition[]) => {
  const types = new Set(conditions.map((item) => item.type));
  return types.has("country") && types.has("genre") && types.has("actor") && types.has("year");
};

interface ConditionSource {
  rating: Rating | null;
  title: string;
  id: EntityId;
  mediaType: MediaType;
  conditions: QuestCondition[];
}

const sourceHasEnoughMetadata = (source: ConditionSource, count: number, requireLegendaryCore: boolean) =>
  source.conditions.length >= count &&
  uniqueConditionFilterCount(source.conditions) >= count &&
  (!requireLegendaryCore || hasLegendaryCore(source.conditions));

const fallbackSources = (watchedFilmIds = new Set<string>()): ConditionSource[] =>
  FALLBACK_SOURCE_FILMS
    .filter((film) => !watchedFilmIds.has(getFilmIdKey(film.id)))
    .map((film): ConditionSource => ({
      rating: null,
      title: film.title,
      id: film.id || "",
      mediaType: "movie",
      conditions: uniqueBy(film.conditions, (item) => `${item.type}:${item.value}`)
    }));

const takeConditionByType = (pool: QuestCondition[], type: QuestConditionType) => {
  const options = pool.filter((item) => item.type === type);
  const next = randomItem(options);
  if (!next) return null;
  pool.splice(pool.findIndex((item) => item.type === next.type && item.value === next.value), 1);
  return next;
};

const buildConditionSources = (ratingPool: Rating[], count: number, requireLegendaryCore: boolean) =>
  ratingPool
    .map((rating: Rating): ConditionSource => ({
      rating,
      title: getRatingTitle(rating),
      id: getRatingId(rating),
      mediaType: getRatingMediaType(rating),
      conditions: conditionsFromRating(rating)
    }))
    .filter((source) => sourceHasEnoughMetadata(source, count, requireLegendaryCore));

const pickConditions = (ratingPool: Rating[], count: number, {
  requireLegendaryCore = false,
  watchedFilmIds = new Set<string>()
}: { requireLegendaryCore?: boolean; watchedFilmIds?: Set<string> } = {}): QuestCardCriteria & { sourceFilmTitle: string } => {
  const preferredSources = buildConditionSources(ratingPool, count, requireLegendaryCore);
  const realFilmFallbacks = fallbackSources(watchedFilmIds)
    .filter((source) => sourceHasEnoughMetadata(source, count, requireLegendaryCore));
  const source = randomItem(preferredSources.length ? preferredSources : realFilmFallbacks);
  const pool = [...(source?.conditions || realFilmFallbacks[0]?.conditions || [])];
  const chosen: QuestCondition[] = [];
  const usedFilterKeys = new Set<string>();

  if (requireLegendaryCore) {
    (["country", "genre", "year", "actor"] as const).forEach((type) => {
      const next = takeConditionByType(pool, type);
      if (next) {
        chosen.push(next);
        usedFilterKeys.add(conditionFilterKey(next));
      }
    });
  }

  while (chosen.length < count && pool.length > 0) {
    const available = pool.filter((item) => !usedFilterKeys.has(conditionFilterKey(item)));
    const next = randomItem(available);
    if (!next) break;
    chosen.push(next);
    usedFilterKeys.add(conditionFilterKey(next));
    pool.splice(pool.findIndex((item) => item.type === next.type && item.value === next.value), 1);
  }

  return {
    conditions: chosen,
    sourceFilmTitle: source?.title || "",
    sourceFilmId: source?.id || "",
    sourceFilmMediaType: source?.mediaType || ""
  };
};

const buildCardTitle = (conditions: QuestCondition[]) =>
  conditions.length === 1
    ? `Фильм: ${conditions[0].label}`
    : conditions.map((item) => item.label).join(" · ");

export const generateWeeklyQuestCards = ({
  ratings = [],
  sourceRatings = [],
  legendaryMisses = 0,
  now = new Date()
}: { ratings?: Rating[]; sourceRatings?: Rating[]; legendaryMisses?: number; now?: Date } = {}) => {
  const chance = getLegendaryChance(legendaryMisses);
  const legendaryDropped = Math.random() < chance;
  const legendarySlot = legendaryDropped
    ? Math.floor(Math.random() * WEEKLY_QUEST_CARD_LIMITS.cardsPerWeek)
    : -1;
  const watchedFilmIds = new Set(asArray(ratings).map((rating: Rating) => getFilmIdKey(getRatingId(rating))).filter(Boolean));
  const sourcePool = uniqueRatingSources(sourceRatings)
    .filter((rating: Rating) => {
      const filmId = getFilmIdKey(getRatingId(rating));
      return filmId &&
        !watchedFilmIds.has(filmId) &&
        isGameProgressEligible(rating) &&
        conditionsFromRating(rating).length > 0;
    });
  const preferredSourcePool = sourcePool.length >= 5 ? sourcePool : [];

  const cards = Array.from({ length: WEEKLY_QUEST_CARD_LIMITS.cardsPerWeek }, (_, index): WeeklyQuestCard => {
    const rarity = pickRarity(index === legendarySlot);
    const rarityConfig = WEEKLY_QUEST_RARITIES[rarity];
    const conditionCount = rarity === "legendary"
      ? 4 + (Math.random() < 0.35 ? 1 : 0)
      : rarityConfig.conditionCount;
    const {
      conditions,
      sourceFilmTitle,
      sourceFilmId,
      sourceFilmMediaType
    } = pickConditions(preferredSourcePool, conditionCount, {
      requireLegendaryCore: rarity === "legendary",
      watchedFilmIds
    });
    return {
      id: `${getCurrentWeekId(now)}-${index + 1}`,
      rarity,
      title: buildCardTitle(conditions),
      description: `Посмотреть фильм: ${conditions.map((item) => item.label).join(" + ")}`,
      reward: rarityConfig.reward,
      conditions,
      sourceFilmTitle,
      sourceFilmId,
      sourceFilmMediaType
    };
  });

  return { cards, legendaryDropped, legendaryChance: chance };
};

const ratingTime = (rating: Rating) => {
  const value = rating?.firstTrackedAt || rating?.firstRatedAt || rating?.ratedAt;
  return toQuestRatingMillis(value);
};

const tokenSetMatches = (left: string, right: string) => {
  const leftTokens = normalizeText(left).split(" ").filter((item) => item.length > 1);
  const rightTokens = normalizeText(right).split(" ").filter((item) => item.length > 1);
  if (leftTokens.length < 2 || rightTokens.length < 2 || leftTokens.length !== rightTokens.length) {
    return false;
  }
  const rightSet = new Set(rightTokens);
  return leftTokens.every((token) => rightSet.has(token));
};

const textMatches = (values: string[], conditionItem: QuestCondition, { allowPartial = true, allowTokenSet = false }: { allowPartial?: boolean; allowTokenSet?: boolean } = {}) => {
  const wanted = [conditionItem.value, ...(conditionItem.aliases || [])]
    .map(normalizeText)
    .filter(Boolean);
  return values
    .map(normalizeText)
    .some((value) => wanted.some((item) => {
      if (allowTokenSet && tokenSetMatches(value, item)) return true;
      if (!allowPartial || item.length <= 3 || value.length <= 3) return value === item;
      return value === item || value.includes(item) || item.includes(value);
    }));
};

const getConditionPersonAliasGroup = (conditionItem: QuestCondition) => {
  const wanted = [conditionItem.value, ...(conditionItem.aliases || [])]
    .map(normalizeText)
    .filter(Boolean);
  return PERSON_ALIAS_GROUPS.find((group) =>
    group.some((alias) =>
      wanted.some((item) =>
        item === normalizeText(alias) ||
        item.includes(normalizeText(alias)) ||
        normalizeText(alias).includes(item)
      )
    )
  ) || [];
};

const conditionPersonIds = (conditionItem: QuestCondition) => [
  conditionItem.personId,
  conditionItem.person_id,
  conditionItem.tmdbId,
  conditionItem.tmdb_id,
  conditionItem.id
].map(normalizePersonId).filter(Boolean);

const personMatches = (people: PersonInput[], conditionItem: QuestCondition) => {
  const wantedIds = conditionPersonIds(conditionItem);
  if (
    wantedIds.length > 0 &&
    asArray(people).some((person) => wantedIds.includes(normalizePersonId(getPersonId(person))))
  ) {
    return true;
  }

  return textMatches(getPeopleNames(people), {
    ...conditionItem,
    aliases: [
      ...(conditionItem.aliases || []),
      ...getConditionPersonAliasGroup(conditionItem)
    ]
  }, { allowTokenSet: true });
};

const isWeeklyQuestRatingEligible = (rating: Rating = {}) => {
  if (!rating || rating.active === false) return false;
  if (isGameProgressEligible(rating)) return true;
  return Boolean(
    rating.manualWatchedAt &&
    !hasImportMarker(rating) &&
    !isShortFilm(rating)
  );
};

export const ratingMatchesWeeklyCard = (rating: Rating, card: QuestCardCriteria | null | undefined) => {
  if (!rating || !card?.conditions?.length) return false;
  if (rating.active === false) return false;
  if (!isWeeklyQuestRatingEligible(rating)) return false;
  const sourceFilmId = getFilmIdKey(card.sourceFilmId);
  if (sourceFilmId && getFilmIdKey(getRatingId(rating)) === sourceFilmId) {
    const sourceMediaType = card.sourceFilmMediaType || card.mediaType || "";
    if (!sourceMediaType || sourceMediaType === getRatingMediaType(rating)) {
      return true;
    }
  }
  return card.conditions.every((item) => {
    if (item.type === "genre") return textMatches(getGenres(rating), item, { allowPartial: false });
    if (item.type === "country") return textMatches(getCountries(rating), item);
    if (item.type === "director") return personMatches(getDirectors(rating), item);
    if (item.type === "actor") return personMatches(getActors(rating), item);
    if (item.type === "composer") return personMatches(getComposers(rating), item);
    if (item.type === "year") return getYear(rating) === Number(item.value);
    if (item.type === "decade") {
      const year = getYear(rating);
      return year ? Math.floor(year / 10) * 10 === Number(item.value) : false;
    }
    return false;
  });
};

export const isWeeklyCardRevocationAuthoritative = ({
  card,
  completedAt,
  ratings = [],
  ratingHistory = []
}: { card?: QuestCardCriteria | null; completedAt?: DateValue; ratings?: Rating[]; ratingHistory?: RatingHistoryEntry[] } = {}) => {
  const completionTime = toQuestRatingMillis(completedAt);
  if (!card || !completionTime) return false;

  const liveRatings = asArray(ratings).filter((rating: Rating) => rating?.active !== false);
  if (liveRatings.some((rating: Rating) => ratingMatchesWeeklyCard(rating, card))) {
    return false;
  }

  const liveByFilmId = new Map(
    liveRatings
      .map((rating: Rating): [string, Rating] => [getFilmIdKey(getRatingId(rating)), rating])
      .filter(([filmId]) => Boolean(filmId))
  );

  return asArray(ratingHistory).some((historyEntry: RatingHistoryEntry) => {
    if (!historyEntry || historyEntry.active !== false) return false;
    const deletionTime = getQuestDeletionUpdatedAt(historyEntry);
    if (!deletionTime || deletionTime <= completionTime) return false;
    if (!ratingMatchesWeeklyCard({ ...historyEntry, active: true }, card)) return false;

    const filmId = getFilmIdKey(getRatingId(historyEntry));
    const liveRating = liveByFilmId.get(filmId);
    if (!liveRating) return true;

    const liveLifecycleId = String(liveRating.ratingLifecycleId || "");
    const deletedLifecycleId = String(historyEntry.ratingLifecycleId || "");
    const sameLifecycle =
      !liveLifecycleId ||
      !deletedLifecycleId ||
      liveLifecycleId === deletedLifecycleId;

    return sameLifecycle &&
      getQuestRatingUpdatedAt(liveRating) < deletionTime;
  });
};

export const getWeeklyCardProgress = (card: QuestCardCriteria | null | undefined, ratings: Rating[] = [], selectedAt: number | undefined, weekStart = getWeekStart(), weekEnd = getWeekEnd()) => {
  if (!card || !selectedAt) return { progress: 0, matchingFilm: null };
  const selectedTime = Number(selectedAt) || 0;
  const weekStartTime = weekStart.getTime();
  const weekEndTime = weekEnd.getTime();
  const matchingFilm = asArray(ratings).find((rating: Rating) => {
    const time = ratingTime(rating);
    return time >= Math.max(selectedTime, weekStartTime) &&
      time < weekEndTime &&
      ratingMatchesWeeklyCard(rating, card);
  });
  return {
    progress: matchingFilm ? 1 : 0,
    matchingFilm: matchingFilm || null
  };
};

const getWeekWindowFromId = (weekId: string) => {
  const match = /^(\d{4})-W(\d{2})$/.exec(String(weekId || ""));
  if (!match) return null;
  const year = Number(match[1]);
  const week = Number(match[2]);
  if (!Number.isInteger(year) || !Number.isInteger(week) || week < 1 || week > 53) {
    return null;
  }

  const firstWeekStart = getWeekStart(new Date(year, 0, 4));
  const weekStart = new Date(firstWeekStart);
  weekStart.setDate(firstWeekStart.getDate() + (week - 1) * 7);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 7);
  return { weekStart, weekEnd };
};

export const getWeeklyQuestLegendaryStats = (
  weeklyStates: WeeklyQuestState[] = [],
  ratings: Rating[] = [],
  currentWeekId = getCurrentWeekId()
) => {
  const states = asArray(weeklyStates)
    .filter((state) => state?.weekId && isUnknownArray(state.cards))
    .sort((left, right) => String(left.weekId).localeCompare(String(right.weekId)));
  let completedCount = 0;

  states.forEach((state) => {
    const window = getWeekWindowFromId(state.weekId);
    if (!window) return;
    const rewardedSet = new Set(asArray(state.rewardedQuestIds));
    const selectedSet = new Set(asArray(state.selectedQuestIds));

    asArray(state.cards).forEach((card) => {
      if (
        card?.rarity !== "legendary" ||
        !rewardedSet.has(card.id) ||
        !selectedSet.has(card.id)
      ) {
        return;
      }
      const progress = getWeeklyCardProgress(
        card,
        ratings,
        state.selectedAtByCardId?.[card.id],
        window.weekStart,
        window.weekEnd
      );
      if (progress.progress >= 1) completedCount += 1;
    });
  });

  let misses = 0;
  const generatedThroughCurrentWeek = states.filter((state) =>
    String(state.weekId).localeCompare(String(currentWeekId)) <= 0
  );
  for (let index = generatedThroughCurrentWeek.length - 1; index >= 0; index -= 1) {
    if (generatedThroughCurrentWeek[index].legendaryDropped === true) break;
    misses += 1;
  }

  return { completedCount, misses };
};
